/**
 * FathomVoyage — Bunker Price Monitor
 * Scraper Service — ScrapingBee → Ship & Bunker
 *
 * Target: https://shipandbunker.com/prices
 * Cache TTL: 4 hours (bunker prices update ~twice daily)
 *
 * Usage:
 *   const data = await scrapeShipAndBunker();
 */

import { PORTS_DATA } from './mockData.js';

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

const CONFIG = {
  scrapingBeeApiKey: import.meta.env.VITE_SCRAPINGBEE_API_KEY || '',
  scrapingBeeBase:   'https://app.scrapingbee.com/api/v1/',
  targetUrl:         'https://shipandbunker.com/prices',
  cacheTtlMs:        4 * 60 * 60 * 1000,  // 4 hours
  maxRetries:        3,
  retryDelayMs:      1000,                 // doubles on each retry
};

// ---------------------------------------------------------------------------
// In-memory cache (replace with Redis in production)
// ---------------------------------------------------------------------------

const cache = {
  data:      null,
  fetchedAt: null,

  isValid() {
    return this.data && this.fetchedAt &&
      (Date.now() - this.fetchedAt < CONFIG.cacheTtlMs);
  },

  set(data) {
    this.data      = data;
    this.fetchedAt = Date.now();
  },

  getAge() {
    if (!this.fetchedAt) return null;
    return Math.round((Date.now() - this.fetchedAt) / (60 * 60 * 1000));
  },
};

// ---------------------------------------------------------------------------
// ScrapingBee fetch with retry + exponential backoff
// ---------------------------------------------------------------------------

async function fetchWithRetry(url, attempt = 1) {
  const params = new URLSearchParams({
    api_key:        CONFIG.scrapingBeeApiKey,
    url:            url,
    render_js:      'false',          // S&B price tables are server-rendered
    premium_proxy:  'false',
    country_code:   'us',
    return_page_as: 'markdown',       // easier to parse than raw HTML
  });

  const scrapingBeeUrl = `${CONFIG.scrapingBeeBase}?${params}`;

  try {
    const response = await fetch(scrapingBeeUrl, {
      method:  'GET',
      headers: { 'Accept': 'text/plain' },
      signal:  AbortSignal.timeout(30_000),
    });

    if (!response.ok) {
      throw new Error(`ScrapingBee returned ${response.status}: ${response.statusText}`);
    }

    return await response.text();

  } catch (err) {
    if (attempt >= CONFIG.maxRetries) {
      throw new Error(`Scrape failed after ${CONFIG.maxRetries} attempts: ${err.message}`);
    }

    const delay = CONFIG.retryDelayMs * Math.pow(2, attempt - 1);
    console.warn(`[Scraper] Attempt ${attempt} failed. Retrying in ${delay}ms...`);
    await new Promise(resolve => setTimeout(resolve, delay));
    return fetchWithRetry(url, attempt + 1);
  }
}

// ---------------------------------------------------------------------------
// HTML/Markdown parser — extracts price table from Ship & Bunker
// ---------------------------------------------------------------------------

/**
 * Parses Ship & Bunker markdown output.
 *
 * Ship & Bunker page structure (as of April 2026):
 *   - Global port price table with columns: Port | VLSFO | HSFO | MGO | Change
 *   - Individual port pages at: /prices/bunkerworld/{port-slug}
 *
 * NOTE: The exact selectors here will need updating if S&B restructures their HTML.
 * The scraper is intentionally built to fail loudly (not silently) so staleness
 * is always visible to the operator.
 */
function parseShipAndBunkerMarkdown(markdown) {
  const portPrices = {};

  // Match price table rows: | Port Name | $XXX | $XXX | $XXX |
  // S&B markdown format: | Port       | VLSFO  | HSFO   | MGO    |
  const tableRowRegex = /\|\s*([^|]+?)\s*\|\s*\$?([\d,.]+|-)\s*\|\s*\$?([\d,.]+|-)\s*\|\s*\$?([\d,.]+|-)\s*\|/g;

  // Port name → our port ID mapping
  const portNameMap = {
    'Singapore':           'SGP',
    'Fujairah':            'FUJ',
    'Rotterdam':           'RTM',
    'Houston':             'HOU',
    'Piraeus':             'PIR',
    'Gibraltar':           'GIB',
    'Algeciras':           'ALG',
    'Panama':              'PAN',
    'Durban':              'DUR',
    'Cape Town':           'CPT',
    'Colombo':             'CMB',
    'Jeddah':              'JED',
    'Mumbai':              'MUM',
    'Shanghai':            'SHA',
    'Busan':               'BUS',
    'Hong Kong':           'HKG',
    'Kaohsiung':           'KHH',
    'Balboa':              'BAL',
    'Las Palmas':          'LPA',
    'Tanjung Pelepas':     'TPP',
  };

  let match;
  while ((match = tableRowRegex.exec(markdown)) !== null) {
    const [, portName, vlsfo, hsfo, mgo] = match;
    const portId = portNameMap[portName.trim()];

    if (!portId) continue;

    const parsePrice = (str) => {
      const cleaned = str.replace(/[,$]/g, '').trim();
      return cleaned === '-' || cleaned === '' ? null : parseFloat(cleaned);
    };

    portPrices[portId] = {
      VLSFO: parsePrice(vlsfo),
      HSFO:  parsePrice(hsfo),
      MGO:   parsePrice(mgo),
      LNG:   null,  // LNG not in main S&B table — fetch from individual port pages
    };
  }

  if (Object.keys(portPrices).length === 0) {
    throw new Error('Parser extracted 0 ports — S&B page structure may have changed');
  }

  return portPrices;
}

// ---------------------------------------------------------------------------
// Normalize scraped prices to standard format
// ---------------------------------------------------------------------------

function normalizePrices(scrapedPrices) {
  const normalized = {};

  for (const [portId, prices] of Object.entries(scrapedPrices)) {
    const existing = PORTS_DATA[portId];
    if (!existing) continue;

    normalized[portId] = {
      ...existing,  // carry forward static metadata (name, region, flag, lat/lng)
      VLSFO: {
        price:   prices.VLSFO,
        change:  null,  // Change requires yesterday's cached value — computed server-side
        history: existing.VLSFO.history,  // Keep mock history until we have real historical data
      },
      HSFO: {
        price:   prices.HSFO,
        change:  null,
        history: existing.HSFO.history,
      },
      MGO: {
        price:   prices.MGO,
        change:  null,
        history: existing.MGO.history,
      },
      LNG: {
        price:   null,  // Not available from main scrape
        change:  null,
        history: null,
      },
    };
  }

  return normalized;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Scrapes Ship & Bunker for live bunker prices.
 * Returns cached data if within TTL.
 *
 * @returns {Promise<{ports: Object, source: string, lastUpdated: string, cacheAgeHours: number|null}>}
 */
export async function scrapeShipAndBunker() {
  // Serve from cache if valid
  if (cache.isValid()) {
    console.info(`[Scraper] Serving from cache (${cache.getAge()}h old)`);
    return {
      ...cache.data,
      source: 'scrape_cached',
      cacheAgeHours: cache.getAge(),
    };
  }

  console.info('[Scraper] Cache miss — fetching live data from Ship & Bunker');

  if (!CONFIG.scrapingBeeApiKey) {
    throw new Error('SCRAPINGBEE_API_KEY not set. Set it in .env to enable live scraping.');
  }

  const markdown      = await fetchWithRetry(CONFIG.targetUrl);
  const scrapedPrices = parseShipAndBunkerMarkdown(markdown);
  const normalizedData = normalizePrices(scrapedPrices);

  const result = {
    source:      'scrape_live',
    lastUpdated: new Date().toISOString(),
    dataDate:    new Date().toISOString().split('T')[0],
    ports:       normalizedData,
    disclaimer:  null,
  };

  cache.set(result);

  return { ...result, cacheAgeHours: 0 };
}

/**
 * Check if the scraper cache has usable data (stale is ok for fallback)
 */
export function getCachedData() {
  if (!cache.data) return null;
  return { ...cache.data, source: 'scrape_stale', cacheAgeHours: cache.getAge() };
}

export { CONFIG as scraperConfig };
